// SignupNav.jsx
// Design: Exact token match to BrandProfileFormFull.js
// Replicates the sticky progress bar header used at the top of BrandProfileFormFull
// Shows back button + step title + optional step counter and progress bar

import React from "react";
import { Box, IconButton, Typography, LinearProgress } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

// ─── Exact tokens from BrandProfileFormFull ──────────────────────────────────
const T = {
  ink:        "#0D0D12",
  inkLight:   "#2A2A35",
  muted:      "#6B6B80",
  border:     "#E4E4EF",
  white:      "#FFFFFF",
  accent:     "#5B5BF6",
  accentSoft: "#EDEDFF",
  accentGlow: "rgba(91,91,246,0.12)",
  success:    "#22C55E",
};
const fontBody = "'DM Sans', 'Instrument Sans', system-ui, sans-serif";
const fontMono = "'JetBrains Mono', 'Fira Code', monospace";

// ─── SignupNav ────────────────────────────────────────────────────────────────
// Props:
//   title      — page heading shown next to back button (string)
//   backPath   — route to go back to (string, defaults to -1)
//   progress   — 0-100 number to show a completion bar; omit to hide bar entirely
//   step       — e.g. "Step 2 of 3" label (string, optional)
//   subtitle   — smaller text under the title (string, optional)

const SignupNav = ({ title, backPath, progress, step, subtitle }) => {
  const navigate = useNavigate();
  const showBar = typeof progress === "number";

  return (
    <>
      {/* Font import — same as BrandProfileFormFull */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
        .MuiLinearProgress-root { background: ${T.border} !important; border-radius: 99px !important; }
        .MuiLinearProgress-bar  { background: ${T.accent} !important; border-radius: 99px !important; }
      `}</style>

      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 200,
          background: T.white,
          borderBottom: `1px solid ${T.border}`,
          boxShadow: "0 1px 0 rgba(0,0,0,0.04)",
        }}
      >
        {/* Main row */}
        <Box
          sx={{
            maxWidth: 1000,
            mx: "auto",
            px: { xs: 2, md: 3 },
            py: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          {/* Back button — same hover treatment as BrandProfileFormFull's outlined buttons */}
          <IconButton
            onClick={() => navigate(backPath || -1)}
            size="small"
            sx={{
              width: 34, height: 34,
              borderRadius: "10px",
              border: `1px solid ${T.border}`,
              color: T.inkLight,
              background: "transparent",
              flexShrink: 0,
              transition: "border-color 0.15s, background 0.15s, color 0.15s",
              "&:hover": {
                borderColor: T.accent,
                color: T.accent,
                background: T.accentSoft,
              },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: "16px" }} />
          </IconButton>

          {/* Title block */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Step label — mirrors fontMono section labels from BrandProfileFormFull */}
            {step && (
              <Typography sx={{
                fontFamily: fontMono,
                fontSize: "10.5px",
                color: T.accent,
                fontWeight: 600,
                letterSpacing: "0.08em",
                lineHeight: 1,
                mb: 0.4,
              }}>
                {step}
              </Typography>
            )}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <Typography sx={{
                fontFamily: fontBody,
                fontWeight: 700,
                fontSize: "15px",
                color: T.ink,
                lineHeight: 1.2,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}>
                {title || "Back"}
              </Typography>
              {/* Completion badge — appears at 100% */}
              {showBar && progress === 100 && (
                <Box sx={{
                  display: "flex", alignItems: "center", gap: 0.5,
                  px: 1, py: 0.25,
                  borderRadius: "6px",
                  background: "rgba(34,197,94,0.08)",
                  border: "1px solid rgba(34,197,94,0.25)",
                  flexShrink: 0,
                }}>
                  <CheckCircleOutlineIcon sx={{ fontSize: "12px", color: T.success }} />
                  <Typography sx={{ fontFamily: fontMono, fontSize: "10px", color: T.success, fontWeight: 700 }}>
                    Complete
                  </Typography>
                </Box>
              )}
            </Box>
            {subtitle && (
              <Typography sx={{ fontSize: "12px", color: T.muted, fontFamily: fontBody, mt: 0.25, lineHeight: 1.3 }}>
                {subtitle}
              </Typography>
            )}
          </Box>

          {/* Progress % — shown only when bar prop passed */}
          {showBar && (
            <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 0.75 }}>
              {progress === 100 && (
                <CheckCircleOutlineIcon sx={{ fontSize: "13px", color: T.success }} />
              )}
              <Typography sx={{
                fontFamily: fontMono,
                fontSize: "11.5px",
                fontWeight: 700,
                color: progress === 100 ? T.success : T.accent,
              }}>
                {progress}%
              </Typography>
            </Box>
          )}
        </Box>

        {/* Progress bar — exact same as BrandProfileFormFull sticky header */}
        {showBar && (
          <Box sx={{ px: { xs: 2, md: 3 }, pb: 1.5, maxWidth: 1000, mx: "auto" }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{ height: 4, borderRadius: 99 }}
            />
          </Box>
        )}
      </Box>
    </>
  );
};

export default SignupNav;